import {useCallback, useEffect, useState} from 'react';
import {
  Box,
  Button,
  Tooltip,
  Autocomplete,
  CircularProgress,
  TextField,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import useDebounce from '../../../components/utlis/useDebounce';
import {companyIdSelector} from '../../../store/Slices/companySlice';
import {useStoreSelector} from '../../../store/configstore';
import {useRqMediaTracksArtistOrSong} from '../../../react_query/media_tracks/ListMediaTrackPaginatPro';

export type IFuzzyFilters = {
  track_artist: string;
  track_name: string;
  fuzzy: boolean;
};

export type IFuzzyQueryFilters = {
  track_artist: string;
  track_name: string;
};

type Props = {
  trackArtist: string;
  trackName: string;
  onFuzzySearchChange: (filters: IFuzzyFilters) => void;
  onReset: () => void;
};

const FuzzySearchSong = ({
  trackName,
  trackArtist,
  onReset,
  onFuzzySearchChange,
}: Props) => {
  const [artistInput, setArtistInput] = useState(trackArtist);
  const [trackInput, setTrackInput] = useState(trackName);

  const [artistOptions, setArtistOptions] = useState<string[]>([]);
  const [trackOptions, setTrackOptions] = useState<string[]>([]);

  useEffect(() => {
    setArtistInput(trackArtist);
  }, [trackArtist]);

  useEffect(() => {
    setTrackInput(trackName);
  }, [trackName]);

  const companyId = useStoreSelector(companyIdSelector);

  const debouncedArtist = useDebounce(artistInput, 500);
  const debouncedTrack = useDebounce(trackInput, 500);

  const {data: artistData, isFetching: artistLoading} =
    useRqMediaTracksArtistOrSong(
      'track_artist',
      debouncedArtist ?? '',
      debouncedTrack ?? '',
      companyId,
      (debouncedArtist ?? '').length >= 3,
    );

  const {data: trackData, isFetching: trackLoading} =
    useRqMediaTracksArtistOrSong(
      'track_name',
      debouncedTrack ?? '',
      debouncedArtist ?? '',
      companyId,
      (debouncedTrack ?? '').length >= 3,
    );
  useEffect(() => {
    if (artistData?.data?.length) {
      setArtistOptions([...new Set(artistData.data.map(item => item.name))]);
    }
  }, [artistData]);

  useEffect(() => {
    if (trackData?.data?.length) {
      setTrackOptions([...new Set(trackData.data.map(item => item.name))]);
    }
  }, [trackData]);

  const isArtistTooShort =
    artistInput?.trim().length > 0 && artistInput?.trim().length < 3;
  const isTrackTooShort =
    trackInput?.trim().length > 0 && trackInput?.trim().length < 3;

  useEffect(() => {
    const validArtist = (debouncedArtist ?? '').length >= 3;
    const validTrack = (debouncedTrack ?? '').length >= 3;

    onFuzzySearchChange({
      track_artist: validArtist ? debouncedArtist ?? '' : '',
      track_name: validTrack ? debouncedTrack ?? '' : '',
      fuzzy: validArtist || validTrack,
    });
  }, [debouncedArtist, debouncedTrack, onFuzzySearchChange]);

  const handleReset = useCallback(() => {
    setArtistInput('');
    setTrackInput('');
    onReset();
  }, [onReset]);

  return (
    <Box
      className='w-full flex items-center justify-center'
      sx={{columnGap: '10px'}}>
      {/* Artist Autocomplete */}
      <Autocomplete
        freeSolo
        disableClearable
        openOnFocus
        inputValue={artistInput}
        options={isArtistTooShort ? [] : artistOptions}
        loading={artistLoading}
        onInputChange={(_, newInput) => setArtistInput(newInput)}
        onChange={(_, newValue) => {
          if (typeof newValue === 'string') setArtistInput(newValue);
        }}
        noOptionsText={
          isArtistTooShort
            ? 'Type at least 3 characters to search'
            : 'No artist found'
        }
        renderOption={(props, option) => (
          <li {...props} key={option}>
            {option}
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label='Search Artist'
            type='search'
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {artistLoading && (
                    <CircularProgress color='inherit' size={16} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{minWidth: 200, flex: 1}}
      />

      {/* Track Autocomplete */}
      <Autocomplete
        freeSolo
        disableClearable
        openOnFocus
        options={trackOptions}
        loading={trackLoading}
        inputValue={trackInput}
        onInputChange={(_, newInput) => setTrackInput(newInput)}
        onChange={(_, newValue) => {
          if (typeof newValue === 'string') setTrackInput(newValue);
        }}
        noOptionsText={
          isTrackTooShort
            ? 'Type at least 3 characters to search'
            : 'No song found'
        }
        renderInput={params => (
          <TextField
            {...params}
            label='Search Song'
            type='search'
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {trackLoading ? (
                    <CircularProgress color='inherit' size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{minWidth: 200, flex: 1}}
      />
      {/* <ReplayIcon /> */}
      <Tooltip title='Reset' arrow>
        <span>
          <Button
            variant='outlined'
            color='primary'
            onClick={handleReset}
            sx={{
              minWidth: 'max-content',
              alignItems: 'center',
              display: 'flex',
              '& .MuiButton-startIcon': {
                margin: 0,
              },
            }}
            startIcon={
              <ReplayIcon
                sx={{
                  fontSize: 22,
                }}
              />
            }
            disabled={artistInput.length === 0 && trackInput.length === 0}
          />
        </span>
      </Tooltip>
    </Box>
  );
};

export default FuzzySearchSong;
